// src/pages/TasksPage.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Typography, message, Space, Tooltip, Tag, DatePicker } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';
import { mockTasks } from '../data/mockTasks';
import { mockClients, mockUsers } from '../data/mockData';
import { mockProjects } from '../data/mockProjects';
import { Task } from '../types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const TasksPage: React.FC = () => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const [tasks, setTasks] = useState<Task[]>(mockTasks);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  // Filtrar tareas según el rol
  const filteredTasks = currentUser?.role === 'admin'
    ? tasks
    : tasks.filter(t => t.assignedTo === currentUser?.id || t.clientId === currentUser?.clientId);

  const getClientName = (clientId?: string) => {
    if (!clientId) return 'N/A';
    const client = mockClients.find(c => c.id === clientId);
    return client?.companyName || 'N/A';
  };

  const getProjectName = (projectId?: string) => {
    if (!projectId) return 'N/A';
    const project = mockProjects.find(p => p.id === projectId);
    return project?.name || 'N/A';
  };

  const getUserName = (userId: string) => {
    const user = mockUsers.find(u => u.id === userId);
    return user?.name || 'N/A';
  };

  const getStatusTag = (status: Task['status']) => {
    const config = {
      'pending': { color: 'default', text: 'Pendiente', icon: <ClockCircleOutlined /> },
      'in-progress': { color: 'processing', text: 'En Progreso', icon: <ClockCircleOutlined /> },
      'completed': { color: 'success', text: 'Completada', icon: <CheckCircleOutlined /> },
    };
    const { color, text, icon } = config[status];
    return <Tag color={color} icon={icon}>{text}</Tag>;
  };

  const getPriorityTag = (priority: Task['priority']) => {
    const config = {
      'low': { color: 'default', text: 'Baja' },
      'medium': { color: 'warning', text: 'Media' },
      'high': { color: 'error', text: 'Alta' },
    };
    const { color, text } = config[priority];
    return <Tag color={color}>{text}</Tag>;
  };

  const isOverdue = (dueDate: string, status: Task['status']) => {
    if (status === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  const columns: ColumnsType<Task> = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Task) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {isOverdue(record.dueDate, record.status) && (
            <Tag color="red" style={{ fontSize: 11 }}>Vencida</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Asignado a',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (userId: string) => getUserName(userId),
    },
    ...(currentUser?.role === 'admin' ? [{
      title: 'Cliente',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (clientId?: string) => getClientName(clientId),
    }] : []),
    {
      title: 'Prioridad',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: Task['priority']) => getPriorityTag(priority),
      filters: [
        { text: 'Alta', value: 'high' },
        { text: 'Media', value: 'medium' },
        { text: 'Baja', value: 'low' },
      ],
      onFilter: (value, record) => record.priority === value,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: Task['status']) => getStatusTag(status),
      filters: [
        { text: 'Pendiente', value: 'pending' },
        { text: 'En Progreso', value: 'in-progress' },
        { text: 'Completada', value: 'completed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Fecha Límite',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string, record: Task) => (
        <Text type={isOverdue(date, record.status) ? 'danger' : undefined}>
          {new Date(date).toLocaleDateString('es-ES')}
        </Text>
      ),
      sorter: (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, record: Task) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewTask(record)}
            />
          </Tooltip>
          {currentUser?.role === 'admin' && (
            <>
              <Tooltip title="Editar">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEditTask(record)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteTask(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleAddTask = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = (values: any) => {
    const newTask: Task = {
      id: `t${tasks.length + 1}`,
      title: values.title,
      description: values.description,
      clientId: values.clientId,
      projectId: values.projectId,
      assignedTo: values.assignedTo,
      dueDate: values.dueDate.toISOString(),
      status: values.status,
      priority: values.priority,
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.id || 'u1',
    };
    
    setTasks([...tasks, newTask]);
    message.success('Tarea creada exitosamente');
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleViewTask = (task: Task) => {
    setSelectedTask(task);
    setIsViewModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    editForm.setFieldsValue({
      ...task,
      dueDate: dayjs(task.dueDate),
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (values: any) => {
    if (!selectedTask) return;
    
    const updatedTasks = tasks.map(t => 
      t.id === selectedTask.id ? {
        ...t,
        title: values.title,
        description: values.description,
        clientId: values.clientId,
        projectId: values.projectId,
        assignedTo: values.assignedTo,
        dueDate: values.dueDate.toISOString(),
        status: values.status,
        priority: values.priority,
      } : t
    );
    
    setTasks(updatedTasks);
    message.success('Tarea actualizada exitosamente');
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedTask(null);
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedTask(null);
  };

  const handleDeleteTask = (task: Task) => {
    modal.confirm({
      title: '¿Está seguro de eliminar esta tarea?',
      icon: <ExclamationCircleOutlined />,
      content: `Se eliminará la tarea "${task.title}". Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        const updatedTasks = tasks.filter(t => t.id !== task.id);
        setTasks(updatedTasks);
        message.success('Tarea eliminada exitosamente');
      },
    });
  };

  const TaskForm = ({ form, onFinish }: any) => (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <Form.Item
        label="Título"
        name="title"
        rules={[{ required: true, message: 'Por favor ingrese el título' }]}
      >
        <Input placeholder="Ej: Subir reglamento interno" />
      </Form.Item>

      <Form.Item
        label="Descripción"
        name="description"
      >
        <Input.TextArea rows={3} placeholder="Descripción de la tarea" />
      </Form.Item>

      <Form.Item
        label="Cliente"
        name="clientId"
      >
        <Select placeholder="Seleccione el cliente (opcional)" allowClear>
          {mockClients.map(client => (
            <Select.Option key={client.id} value={client.id}>
              {client.companyName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Proyecto"
        name="projectId"
      >
        <Select placeholder="Seleccione el proyecto (opcional)" allowClear>
          {mockProjects.map(project => (
            <Select.Option key={project.id} value={project.id}>
              {project.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Asignar a"
        name="assignedTo"
        rules={[{ required: true, message: 'Por favor seleccione un usuario' }]}
      >
        <Select placeholder="Seleccione el usuario">
          {mockUsers.map(user => (
            <Select.Option key={user.id} value={user.id}>
              {user.name} ({user.role === 'admin' ? 'Admin' : 'Cliente'})
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Fecha Límite"
        name="dueDate"
        rules={[{ required: true, message: 'Por favor seleccione la fecha' }]}
      >
        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
      </Form.Item>

      <Form.Item
        label="Prioridad"
        name="priority"
        rules={[{ required: true, message: 'Por favor seleccione la prioridad' }]}
        initialValue="medium"
      >
        <Select placeholder="Seleccione la prioridad">
          <Select.Option value="low">Baja</Select.Option>
          <Select.Option value="medium">Media</Select.Option>
          <Select.Option value="high">Alta</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Estado"
        name="status"
        rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
        initialValue="pending"
      >
        <Select placeholder="Seleccione el estado">
          <Select.Option value="pending">Pendiente</Select.Option>
          <Select.Option value="in-progress">En Progreso</Select.Option>
          <Select.Option value="completed">Completada</Select.Option>
        </Select>
      </Form.Item>
    </Form>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>
          {currentUser?.role === 'admin' ? 'Gestión de Tareas' : 'Mis Tareas'}
        </Title>
        {currentUser?.role === 'admin' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTask}>
            Nueva Tarea
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredTasks}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Modal de Creación */}
      {currentUser?.role === 'admin' && (
        <Modal
          title="Crear Nueva Tarea"
          open={isModalOpen}
          onCancel={handleCancel}
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancelar
            </Button>,
            <Button key="submit" type="primary" onClick={() => form.submit()}>
              Crear Tarea
            </Button>,
          ]}
          width={600}
        >
          <TaskForm form={form} onFinish={handleSubmit} />
        </Modal>
      )}

      {/* Modal de Edición */}
      {currentUser?.role === 'admin' && (
        <Modal
          title="Editar Tarea"
          open={isEditModalOpen}
          onCancel={handleCancelEdit}
          footer={[
            <Button key="cancel" onClick={handleCancelEdit}>
              Cancelar
            </Button>,
            <Button key="submit" type="primary" onClick={() => editForm.submit()}>
              Guardar Cambios
            </Button>,
          ]}
          width={600}
        >
          <TaskForm form={editForm} onFinish={handleEditSubmit} />
        </Modal>
      )}

      {/* Modal de Detalles */}
      <Modal
        title="Detalles de la Tarea"
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Cerrar
          </Button>
        ]}
      >
        {selectedTask && (
          <div>
            <p><strong>Título:</strong> {selectedTask.title}</p>
            <p><strong>Descripción:</strong> {selectedTask.description || 'Sin descripción'}</p>
            <p><strong>Cliente:</strong> {getClientName(selectedTask.clientId)}</p>
            <p><strong>Proyecto:</strong> {getProjectName(selectedTask.projectId)}</p>
            <p><strong>Asignado a:</strong> {getUserName(selectedTask.assignedTo)}</p>
            <p><strong>Prioridad:</strong> {getPriorityTag(selectedTask.priority)}</p>
            <p><strong>Estado:</strong> {getStatusTag(selectedTask.status)}</p>
            <p><strong>Fecha Límite:</strong> {new Date(selectedTask.dueDate).toLocaleDateString('es-ES')}</p>
            <p><strong>Creado por:</strong> {getUserName(selectedTask.createdBy)}</p>
            <p><strong>Fecha de Creación:</strong> {new Date(selectedTask.createdAt).toLocaleDateString('es-ES')}</p>
            {isOverdue(selectedTask.dueDate, selectedTask.status) && (
              <Tag color="red">Esta tarea está vencida</Tag>
            )}
          </div>
        )}
      </Modal>

      {contextHolder}
    </div>
  );
};

export default TasksPage;
